import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { useForm } from "../../../hooks/useForm";
import {
  useCatRespuestasQuery,
  useDictamenMutation,
} from "../../../services/docs";
import { DocumentacionAdicionalComponent } from "../../../components/DocumentacionAdicionalComponent";
import { Spinner } from "../../../components/Spinner";

export const AseguradoraScreen = () => {
  const { siniestro, tramite_id } = useParams();
  const navigate = useNavigate();

  // Queries & Mutations
  const { data, isLoading } = useCatRespuestasQuery({
    siniestro,
    tramite: tramite_id,
  });
  const [dictamen, { isLoading: cargandoBoton }] = useDictamenMutation();

  // Estados Locales
  const [fi_sub_etapa, setFi] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null); // Para UI del archivo

  // Custom Form Hook
  const {
    formulario,
    handleInputChange,
    handleSelectChange,
    handleTexareChange,
    handleChange,
  } = useForm({
    fi_respuesta_aseguradora: 0,
    fc_comentarios_dictamen: "",
    carta_dictamen: "",
    fn_monto_pagado: 0,
    fd_fecha_pago: "01-01-1999", // Valor por defecto de tu backend
    fi_motivos_rechazo: 0,
    tramite_id: Number(tramite_id),
    siniestro: siniestro,
    fc_clave_aseguradora: "",
  });

  const { fi_respuesta_aseguradora } = formulario;

  // Efecto para determinar la Sub Etapa según la respuesta seleccionada
  useEffect(() => {
    if (data && fi_respuesta_aseguradora) {
      let res = data.respuestas.filter(
        (r: any) => r.id === Number(fi_respuesta_aseguradora)
      );
      if (res.length > 0) {
        setFi(res[0].fi_sub_etapa);
      }
    }
  }, [fi_respuesta_aseguradora, data]);

  // Efecto para resetear campos de pago si la respuesta no es "Pago" (Ej. 1)
  useEffect(() => {
    if (+fi_respuesta_aseguradora !== 1) {
      handleChange("fn_monto_pagado", "0");
      handleChange("fd_fecha_pago", "01-01-1999");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fi_respuesta_aseguradora]);

  // Manejador de Archivos (Base64)
  const onFileChange = (e: any) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setFileName(file.name); // Guardamos el nombre para mostrarlo en la UI

      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        handleChange("carta_dictamen", reader.result as string);
      };
    }
  };

  // Enviar Formulario
  const handleAction = async () => {
    if (+formulario.fi_respuesta_aseguradora === 0) {
      toastr.error("Debe seleccionar una respuesta de la Aseguradora");
      return;
    }

    try {
      const resp = await dictamen({
        ...formulario,
        tramite_id: +(tramite_id || 0),
        fi_respuesta_aseguradora: +formulario.fi_respuesta_aseguradora,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Dictamen guardado exitosamente");
        navigate(`/view/siniestro/${siniestro}/${tramite_id}/`);
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err?.data?.message || "Ocurrió un error al guardar el dictamen");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* --- Módulo Condicional: Documentación Adicional --- */}
      {fi_sub_etapa === 3 && (
        <section className="animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="mb-2 flex items-center gap-2">
            <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            <h3 className="text-sm font-bold text-amber-700">Requerimiento de Documentación</h3>
          </div>
          <DocumentacionAdicionalComponent />
        </section>
      )}

      {/* --- Módulo Principal: Dictamen Aseguradora --- */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <svg className="h-4 w-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
            Resolución y Dictamen Oficial
          </h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

            {/* Campo: Respuesta de la Aseguradora */}
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Respuesta de la Aseguradora
              </label>
              <select
                name="fi_respuesta_aseguradora"
                onChange={handleSelectChange}
                value={formulario.fi_respuesta_aseguradora}
                className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
              >
                <option value={0} disabled>Selecciona el dictamen...</option>
                {data?.respuestas?.map((r: any) => (
                  <option key={r.id} value={Number(r.id)}>
                    {r.fc_respuesta}
                  </option>
                ))}
              </select>
            </div>

            {/* Campo: Clave Aseguradora */}
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Clave / Folio Aseguradora
              </label>
              <input
                type="text"
                name="fc_clave_aseguradora"
                placeholder="Ej. AXA-8829-1"
                onChange={handleInputChange}
                value={formulario.fc_clave_aseguradora}
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            {/* Bloque Condicional: Campos de PAGO */}
            {fi_sub_etapa === 1 && (
              <div className="col-span-1 grid grid-cols-1 gap-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 md:col-span-2 md:grid-cols-2 animate-in fade-in zoom-in-95 duration-300">
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                    Monto Pagado Autorizado
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-emerald-600 font-bold">$</span>
                    <input
                      type="number"
                      name="fn_monto_pagado"
                      step="0.01"
                      onChange={handleInputChange}
                      value={formulario.fn_monto_pagado}
                      className="h-11 w-full rounded-xl border border-emerald-200 bg-white pl-7 pr-3 text-sm font-bold text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                    Fecha de Pago / Transferencia
                  </label>
                  <input
                    type="date"
                    name="fd_fecha_pago"
                    id="fd_fecha_pago"
                    onChange={handleInputChange}
                    className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-3 text-sm font-medium text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
              </div>
            )}

            {/* Campo: Comentarios */}
            <div className="md:col-span-2">
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Comentarios / Observaciones del Dictamen
              </label>
              <textarea
                name="fc_comentarios_dictamen"
                onChange={handleTexareChange}
                value={formulario.fc_comentarios_dictamen}
                placeholder="Escribe las observaciones correspondientes a la resolución..."
                rows={3}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
              ></textarea>
            </div>

            {/* Campo: Subida de Archivo (Carta Dictamen) */}
            <div className="md:col-span-2">
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Carta Dictamen Oficial (PDF/IMG)
              </label>
              <div className="flex items-center gap-3">
                <label className="group flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 transition-colors hover:border-brand-400 hover:bg-brand-50">
                  <svg className="h-4 w-4 text-slate-400 group-hover:text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                  <span className="text-xs font-semibold text-slate-600 group-hover:text-brand-700">Seleccionar Archivo</span>
                  <input
                    type="file"
                    name="carta"
                    onChange={onFileChange}
                    className="hidden"
                  />
                </label>
                {fileName && (
                  <span className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 border border-emerald-100">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                    {fileName}
                  </span>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* --- Footer de Acción --- */}
        <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50/50 p-4">
          <button
            onClick={handleAction}
            disabled={cargandoBoton}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-50"
          >
            {cargandoBoton ? <Spinner /> : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path></svg>
                Guardar Resolución Final
              </>
            )}
          </button>
        </div>
      </section>

    </div>
  );
};
